import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ProductTabs from "../components/catalog/ProductTabs";
import { ShoppingCart, Phone, Check, Play, X, ChevronRight, ChevronLeft, Anchor, Gauge, Users, Ruler, Weight, Activity, Maximize2, Home } from "lucide-react";
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';

// Unified Interface
interface ProductDetail {
    id: string;
    Title: string;
    Description: string;
    FullInfo?: string;
    Price?: string;
    Images: string[];
    Category?: string;
    StockCode?: string;
    Brand?: string;

    // Boat specific
    BoatLength?: string;
    BoatWidth?: string;
    BoatCapacity?: string;
    BoatWeight?: string;
    MinMotorPower?: string;
    MaxMotorPower?: string;
    MotorPower?: string;
    DefaultEquipments?: string[];
    ExtraEquipments?: (string | { name: string; price: number })[];
    Videos?: string[];
}

interface ProductPageProps {
    type?: "boat" | "used" | "engine" | "product";
}

const BoatDetailPage: React.FC<ProductPageProps> = ({ type }) => {
    const { slug } = useParams();
    const [product, setProduct] = useState<ProductDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedMedia, setSelectedMedia] = useState<string | null>(null);

    // Configuration State
    const [selectedExtras, setSelectedExtras] = useState<number[]>([]);
    const [activeVideo, setActiveVideo] = useState<string | null>(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    const navigateImage = (direction: 'prev' | 'next') => {
        if (!product || product.Images.length === 0) return;
        const currentIdx = product.Images.indexOf(selectedMedia || product.Images[0]);
        const newIdx = direction === 'next'
            ? (currentIdx + 1) % product.Images.length
            : (currentIdx - 1 + product.Images.length) % product.Images.length;
        setSelectedMedia(product.Images[newIdx]);
    };

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchProduct = async () => {
            let table = "";
            const path = window.location.pathname;

            if (type === "engine" || path.includes("/motor/")) {
                table = 'engines';
            } else if (type === "product" || path.includes("/aksesuar/")) {
                table = 'products';
            } else if (path.includes("/ikinciel/")) {
                table = 'used_boats';
            } else {
                table = 'boats';
            }

            setLoading(true);
            const { data, error } = await supabase.from(table).select('*').eq('id', slug).single();

            if (error || !data) {
                console.error("Veri alınamadı", error);
                setLoading(false);
                return;
            }

            const normalized: ProductDetail = {
                id: data.id,
                Title: data.Title || data.title || data.name,
                Description: data.Description || data.description,
                FullInfo: data.FullInfo || data.description,
                Price: data.Price || data.price,
                Images: (data.Images && data.Images.length > 0) ? data.Images : (data.CatalogImage ? [data.CatalogImage] : (data.imageUrl ? [data.imageUrl] : [])),
                Brand: data.brand || data.Brand,
                BoatLength: data.BoatLength,
                BoatWidth: data.BoatWidth,
                BoatCapacity: data.BoatCapacity,
                BoatWeight: data.BoatWeight,
                MinMotorPower: data.MinMotorPower,
                MaxMotorPower: data.MaxMotorPower,
                MotorPower: data.MotorPower,
                DefaultEquipments: data.DefaultEquipments,
                ExtraEquipments: data.ExtraEquipments,
                Videos: data.Videos || []
            };

            setProduct(normalized);
            if (normalized.Images.length > 0) setSelectedMedia(normalized.Images[0]);
            setLoading(false);
        };

        fetchProduct();
    }, [slug, type]);

    const toggleExtra = (index: number) => {
        if (selectedExtras.includes(index)) {
            setSelectedExtras(selectedExtras.filter(i => i !== index));
        } else {
            setSelectedExtras([...selectedExtras, index]);
        }
    };

    const calculateTotalPrice = () => {
        if (!product?.Price) return null;
        let base = parseFloat(product.Price.replace(/\./g, '').replace(',', '.'));
        if (isNaN(base)) return product.Price;

        let extrasTotal = 0;
        product.ExtraEquipments?.forEach((extra, idx) => {
            if (selectedExtras.includes(idx) && typeof extra !== 'string') {
                extrasTotal += extra.price || 0;
            }
        });

        return (base + extrasTotal).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    if (loading) return (
        <div className="flex justify-center items-center min-h-screen bg-white">
            <div className="flex flex-col items-center">
                <div className="w-16 h-16 border-4 border-[#002744] border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium tracking-wider">YÜKLENİYOR...</p>
            </div>
        </div>
    );

    if (!product) return <div className="p-20 text-center text-xl font-light">Ürün bulunamadı.</div>;

    const isBoat = type === 'boat' || !!product.BoatLength;
    const totalPrice = calculateTotalPrice();

    const getExtraInfo = (extra: string | { name: string; price: number }) => {
        if (typeof extra === 'string') return { name: extra, price: 0 };
        return extra;
    };

    const tabs = [
        {
            id: 'info',
            label: 'AÇIKLAMA',
            content: (
                <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed font-light">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.Description) }} />
                    {product.FullInfo && <div className="mt-8 pt-8 border-t" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.FullInfo) }} />}
                </div>
            )
        },
        ...(product.BoatLength || product.MinMotorPower || product.MotorPower ? [{
            id: 'specs',
            label: 'TEKNİK ÖZELLİKLER',
            content: (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 text-sm">
                    {product.BoatLength && <div className="flex justify-between items-center py-3 border-b border-gray-100"><span className="text-gray-500 uppercase tracking-widest text-xs">Uzunluk</span> <span className="font-bold text-lg text-gray-900">{product.BoatLength} m</span></div>}
                    {product.BoatWidth && <div className="flex justify-between items-center py-3 border-b border-gray-100"><span className="text-gray-500 uppercase tracking-widest text-xs">Genişlik</span> <span className="font-bold text-lg text-gray-900">{product.BoatWidth} m</span></div>}
                    {product.BoatCapacity && <div className="flex justify-between items-center py-3 border-b border-gray-100"><span className="text-gray-500 uppercase tracking-widest text-xs">Kapasite</span> <span className="font-bold text-lg text-gray-900">{product.BoatCapacity} Kişi</span></div>}
                    {product.BoatWeight && <div className="flex justify-between items-center py-3 border-b border-gray-100"><span className="text-gray-500 uppercase tracking-widest text-xs">Ağırlık</span> <span className="font-bold text-lg text-gray-900">{product.BoatWeight} kg</span></div>}
                    {product.MotorPower
                        ? <div className="flex justify-between items-center py-3 border-b border-gray-100"><span className="text-gray-500 uppercase tracking-widest text-xs">Motor Gücü</span> <span className="font-bold text-lg text-gray-900">{product.MotorPower}</span></div>
                        : <>
                            {product.MinMotorPower && <div className="flex justify-between items-center py-3 border-b border-gray-100"><span className="text-gray-500 uppercase tracking-widest text-xs">Min. Motor</span> <span className="font-bold text-lg text-gray-900">{product.MinMotorPower} HP</span></div>}
                            {product.MaxMotorPower && <div className="flex justify-between items-center py-3 border-b border-gray-100"><span className="text-gray-500 uppercase tracking-widest text-xs">Maks. Motor</span> <span className="font-bold text-lg text-gray-900">{product.MaxMotorPower} HP</span></div>}
                        </>
                    }
                </div>
            )
        }] : []),
    ];

    return (
        <div className="bg-white min-h-screen font-montserrat text-gray-900 selection:bg-blue-100 selection:text-blue-900">

            {/* PREMIUM GALLERY HERO */}
            <div className="bg-gradient-to-b from-gray-50 via-white to-white">
                <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-4">

                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-2 text-xs text-gray-500 mb-5 uppercase tracking-wider">
                        <a href="/" className="hover:text-[#002744] transition-colors flex items-center gap-1">
                            <Home size={12} /> Anasayfa
                        </a>
                        <ChevronRight size={12} className="text-gray-300" />
                        <a href={type === 'used' ? '/urunler/ikinciel' : '/urunler/tekne-ve-yatlar'} className="hover:text-[#002744] transition-colors">
                            {type === 'used' ? 'İkinci El Tekneler' : 'Tekneler'}
                        </a>
                        <ChevronRight size={12} className="text-gray-300" />
                        <span className="text-[#002744] font-semibold truncate">{product.Title}</span>
                    </nav>

                    {/* Gallery: Main Image + Vertical Thumbnails */}
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_110px] gap-3 md:gap-4">

                        {/* Main Image */}
                        <div className="relative group bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl overflow-hidden aspect-[16/10] shadow-[0_20px_60px_-15px_rgba(0,39,68,0.25)] border border-gray-100">
                            <img
                                src={selectedMedia || product.Images[0]}
                                alt={product.Title}
                                className="w-full h-full object-contain cursor-zoom-in transition-opacity duration-500"
                                onClick={() => setIsLightboxOpen(true)}
                            />

                            {/* Navigation Arrows */}
                            {product.Images.length > 1 && (
                                <>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
                                        className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/95 backdrop-blur shadow-lg flex items-center justify-center text-[#002744] hover:bg-white hover:scale-110 transition-all md:opacity-0 md:group-hover:opacity-100"
                                        aria-label="Önceki resim"
                                    >
                                        <ChevronLeft size={22} strokeWidth={2.5} />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
                                        className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/95 backdrop-blur shadow-lg flex items-center justify-center text-[#002744] hover:bg-white hover:scale-110 transition-all md:opacity-0 md:group-hover:opacity-100"
                                        aria-label="Sonraki resim"
                                    >
                                        <ChevronRight size={22} strokeWidth={2.5} />
                                    </button>
                                </>
                            )}

                            {/* Image Counter */}
                            {product.Images.length > 1 && (
                                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                                    {Math.max(0, product.Images.indexOf(selectedMedia || product.Images[0])) + 1} / {product.Images.length}
                                </div>
                            )}

                            {/* Zoom Hint */}
                            <button
                                onClick={() => setIsLightboxOpen(true)}
                                className="absolute bottom-4 right-4 bg-black/60 hover:bg-black/80 backdrop-blur text-white text-xs font-medium px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1.5"
                            >
                                <Maximize2 size={12} /> Tam Ekran
                            </button>

                            {/* Type Badge */}
                            <div className="absolute top-4 left-4">
                                <span className="inline-block px-3 py-1.5 bg-[#002744] text-white text-[10px] font-bold tracking-widest uppercase rounded-full shadow-md">
                                    {type === 'used' ? '★ İkinci El' : '✦ Yeni'}
                                </span>
                            </div>
                        </div>

                        {/* Vertical Thumbnail List (desktop) */}
                        {product.Images.length > 1 && (
                            <div className="hidden lg:flex flex-col gap-2 max-h-[500px] overflow-y-auto pr-1 scrollbar-thin">
                                {product.Images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedMedia(img)}
                                        className={`relative h-[72px] flex-shrink-0 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                                            selectedMedia === img
                                                ? 'border-[#002744] shadow-md ring-2 ring-[#002744]/20'
                                                : 'border-transparent opacity-60 hover:opacity-100 hover:border-gray-300'
                                        }`}
                                    >
                                        <img src={img} alt={`Görsel ${idx + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mobile Thumbnail Strip */}
                    {product.Images.length > 1 && (
                        <div className="lg:hidden flex gap-2 mt-3 overflow-x-auto pb-2 -mx-4 px-4">
                            {product.Images.map((img, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setSelectedMedia(img)}
                                    className={`w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                                        selectedMedia === img
                                            ? 'border-[#002744] ring-2 ring-[#002744]/20'
                                            : 'border-transparent opacity-60'
                                    }`}
                                >
                                    <img src={img} alt={`Görsel ${idx + 1}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Title Block */}
                    <div className="mt-10 md:mt-14 mb-4 max-w-4xl">
                        {product.Brand && (
                            <div className="text-sm text-gray-500 uppercase tracking-[0.2em] mb-3 font-medium">
                                {product.Brand}
                            </div>
                        )}
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-[#002744] mb-4 leading-[1.05]">
                            {product.Title}
                        </h1>
                        <div className="w-16 h-1 bg-gradient-to-r from-[#009fe3] to-[#002744] rounded-full mb-5"></div>
                        <p className="text-lg md:text-xl text-gray-600 font-light max-w-3xl leading-relaxed line-clamp-3">
                            {product.Description.replace(/<[^>]*>?/gm, '')}
                        </p>
                    </div>
                </div>
            </div>

            {/* LIGHTBOX MODAL */}
            {isLightboxOpen && (
                <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-200" onClick={() => setIsLightboxOpen(false)}>
                    <button
                        className="absolute top-6 right-6 text-white/80 hover:text-white hover:rotate-90 transition-all duration-300 z-10"
                        onClick={() => setIsLightboxOpen(false)}
                        aria-label="Kapat"
                    >
                        <X size={36} strokeWidth={1.5} />
                    </button>

                    {product.Images.length > 1 && (
                        <>
                            <button
                                onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
                                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur"
                                aria-label="Önceki"
                            >
                                <ChevronLeft size={28} />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
                                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur"
                                aria-label="Sonraki"
                            >
                                <ChevronRight size={28} />
                            </button>
                        </>
                    )}

                    <img
                        src={selectedMedia || ''}
                        alt={product.Title}
                        className="max-w-[92vw] max-h-[88vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {product.Images.length > 1 && (
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-medium">
                            {Math.max(0, product.Images.indexOf(selectedMedia || product.Images[0])) + 1} / {product.Images.length}
                        </div>
                    )}
                </div>
            )}

            <div className="max-w-7xl mx-auto px-6 py-12">
                <div className="flex flex-col lg:flex-row gap-16">

                    {/* LEFT COLUMN: VISUALS & DETAILED INFO */}
                    <div className="lg:w-7/12 space-y-20">

                        {/* Tech Specs Highlights (Icon Grid) */}
                        {isBoat && (product.BoatLength || product.MinMotorPower || product.MotorPower) && (
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                                {product.BoatLength && <div className="flex flex-col items-center p-6 bg-gray-50 rounded-2xl hover:shadow-md transition-shadow">
                                    <Ruler size={32} className="text-[#002744] mb-3 stroke-1" />
                                    <span className="text-xs text-gray-500 uppercase tracking-widest">Uzunluk</span>
                                    <span className="text-xl font-bold text-gray-900">{product.BoatLength}m</span>
                                </div>}
                                {product.BoatCapacity && <div className="flex flex-col items-center p-6 bg-gray-50 rounded-2xl hover:shadow-md transition-shadow">
                                    <Users size={32} className="text-[#002744] mb-3 stroke-1" />
                                    <span className="text-xs text-gray-500 uppercase tracking-widest">Kapasite</span>
                                    <span className="text-xl font-bold text-gray-900">{product.BoatCapacity}</span>
                                </div>}
                                {product.BoatWeight && <div className="flex flex-col items-center p-6 bg-gray-50 rounded-2xl hover:shadow-md transition-shadow">
                                    <Weight size={32} className="text-[#002744] mb-3 stroke-1" />
                                    <span className="text-xs text-gray-500 uppercase tracking-widest">Ağırlık</span>
                                    <span className="text-xl font-bold text-gray-900">{product.BoatWeight}kg</span>
                                </div>}
                                {(product.MotorPower || product.MaxMotorPower) && <div className="flex flex-col items-center p-6 bg-gray-50 rounded-2xl hover:shadow-md transition-shadow">
                                    <Gauge size={32} className="text-[#002744] mb-3 stroke-1" />
                                    <span className="text-xs text-gray-500 uppercase tracking-widest">{product.MotorPower ? 'Motor Gücü' : 'Güç (Max)'}</span>
                                    <span className="text-xl font-bold text-gray-900">{product.MotorPower || `${product.MaxMotorPower}HP`}</span>
                                </div>}
                            </div>
                        )}

                        {/* VIDEO GALLERY SECTION */}
                        {product.Videos && product.Videos.length > 0 && (
                            <div className="space-y-6">
                                <h3 className="text-2xl font-semibold text-[#002744] flex items-center gap-2">
                                    <Play className="fill-current text-blue-600" size={24} />
                                    Video Galeri
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {product.Videos.map((videoSrc, idx) => (
                                        <div
                                            key={idx}
                                            className="group relative cursor-pointer rounded-2xl overflow-hidden aspect-video bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                                            onClick={() => setActiveVideo(videoSrc)}
                                        >
                                            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors z-10"></div>
                                            <video src={videoSrc} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />

                                            <div className="absolute inset-0 flex items-center justify-center z-20">
                                                <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center group-hover:scale-110 transition-all duration-300 group-hover:bg-[#002744] group-hover:text-white">
                                                    <Play className="fill-current w-6 h-6 ml-1 transition-colors text-[#002744] group-hover:text-white" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* INFO CONTENT */}
                        <div className="space-y-8">
                            {isBoat ? (
                                <div className="bg-white">
                                    <h3 className="text-2xl font-semibold mb-6 text-[#002744]">Detaylı İnceleme</h3>
                                    <div className="prose prose-lg max-w-none text-gray-600 font-light">
                                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.FullInfo || product.Description) }} />
                                    </div>

                                    {product.DefaultEquipments && product.DefaultEquipments.length > 0 && (
                                        <div className="mt-12 bg-gray-50 p-8 rounded-2xl border border-gray-100">
                                            <h3 className="text-lg font-bold text-[#002744] mb-6 flex items-center gap-2">
                                                <Anchor className="text-blue-500" /> Standart Donanım
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {product.DefaultEquipments.map((item, idx) => (
                                                    <div key={idx} className="flex items-start gap-3 hover:bg-white p-2 rounded-lg transition-colors">
                                                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 shrink-0">
                                                            <Check size={12} className="text-green-600" />
                                                        </div>
                                                        <span className="text-gray-700 text-sm">{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <ProductTabs tabs={tabs} />
                            )}
                        </div>

                    </div>

                    {/* RIGHT COLUMN: STICKY CONFIGURATOR */}
                    <div className="lg:w-5/12">
                        <div className="sticky top-6 space-y-6">

                            {/* PRICE & ACTION CARD */}
                            <div className="bg-white rounded-3xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.1)] border border-gray-100 overflow-hidden relative">
                                <div className="p-8 bg-gradient-to-br from-[#002744] to-[#0b3c63] text-white relative overflow-hidden">
                                    {/* Decorative elements */}
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 blur-2xl"></div>
                                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/20 rounded-full -ml-10 -mb-10 blur-2xl"></div>

                                    <div className="relative z-10 text-center">
                                        <div className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-2">TOPLAM TUTAR</div>
                                        <div className="text-5xl font-bold mb-1 tracking-tight">{totalPrice || "SORUNUZ"} <span className="text-2xl font-light text-blue-200">₺</span></div>
                                        <div className="text-white/60 text-xs font-light">KDV Hariç Fiyattır</div>
                                    </div>
                                </div>

                                <div className="p-6 space-y-4 bg-white">
                                    <button className="w-full bg-[#002744] hover:bg-blue-900 hover:shadow-lg text-white font-bold py-5 rounded-xl shadow-md transition-all flex items-center justify-center gap-3 group">
                                        <ShoppingCart size={20} className="group-hover:scale-110 transition-transform" />
                                        <span>HEMEN TEKLİF İSTE</span>
                                        <ChevronRight size={16} className="opacity-50 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                    <a href="https://wa.me/905326036152" target="_blank" rel="noopener noreferrer" className="block w-full text-center py-4 rounded-xl border-2 border-green-500 text-green-600 font-bold hover:bg-green-50 transition-colors flex items-center justify-center gap-2">
                                        <Phone size={20} />
                                        <span>Whatsapp</span>
                                    </a>
                                    <p className="text-xs text-center text-gray-400 pt-2">
                                        Güvenli ödeme - Hızlı Teslimat - Garantili
                                    </p>
                                </div>
                            </div>

                            {/* CONFIGURATOR */}
                            {isBoat && product.ExtraEquipments && product.ExtraEquipments.length > 0 && (
                                <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
                                    <div className="bg-gray-50 border-b border-gray-200 p-6 flex justify-between items-center">
                                        <div>
                                            <h3 className="font-bold text-gray-900">Özelleştirme</h3>
                                            <p className="text-xs text-gray-500 mt-1">Teknenizi Ekstra Donanımlar ile Kişiselleştirin</p>
                                        </div>
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <Activity size={16} className="text-blue-600" />
                                        </div>
                                    </div>
                                    <div className="max-h-[500px] overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-200">
                                        {product.ExtraEquipments.map((item, idx) => {
                                            const info = getExtraInfo(item);
                                            const isSelected = selectedExtras.includes(idx);
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => toggleExtra(idx)}
                                                    className={`p-4 mb-2 rounded-xl flex items-center gap-4 cursor-pointer border transition-all duration-200 group ${isSelected ? 'bg-blue-50 border-blue-500 shadow-sm' : 'bg-white border-transparent hover:border-gray-200 hover:bg-gray-50'}`}
                                                >
                                                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'}`}>
                                                        {isSelected && <Check size={14} className="text-white stroke-[3]" />}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className={`text-sm font-semibold transition-colors ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>{info.name}</div>
                                                    </div>
                                                    <div className={`text-sm font-bold whitespace-nowrap ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                                                        {info.price > 0 ? `+${info.price.toLocaleString('tr-TR')} ₺` : 'Fiyat Sorunuz'}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>

            {/* FULLSCREEN VIDEO MODAL */}
            {activeVideo && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-xl animate-in fade-in duration-300">
                    <div
                        className="absolute inset-0"
                        onClick={() => setActiveVideo(null)}
                    ></div>
                    <button
                        className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors transform hover:rotate-90 duration-300"
                        onClick={() => setActiveVideo(null)}
                    >
                        <X size={48} strokeWidth={1} />
                    </button>
                    <div className="w-full max-w-6xl aspect-video bg-black rounded-lg overflow-hidden shadow-2xl relative z-10">
                        <video src={activeVideo} controls autoPlay className="w-full h-full" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BoatDetailPage;
